import { LOGIN_ERROR_CODES } from '@nexus/core';
import { Banner, Button, FormField, Icon, Input, Separator, Skeleton, Spinner } from '@nexus/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useEffectEvent, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import type { LoginCompanies } from '../api/types';
import {
  cancelPasskeyPrompt,
  isPasskeyCancelled,
  passkeyAutofillSupported,
  passkeysSupported,
  signInWithPasskey,
} from '../session/passkey';
import { usePasswordError } from '../session/password';
import { CompanyMark } from '../shell/company-mark';
import { PublicFrame } from './public-frame';

export interface LoginSearch {
  error?: string;
  /** Preselected company, e.g. after a switch to a company of a linked account. */
  company?: string;
  returnTo?: string;
  expired?: boolean;
  /** Back from the page of a password link, the password is set. */
  passwordSet?: boolean;
}

type LoginCompany = LoginCompanies['companies'][number];

export const loginCompaniesQuery = queryOptions({
  queryKey: ['login', 'companies'],
  queryFn: ({ signal }) => api.get<LoginCompanies>('/auth/companies', { signal }),
  staleTime: 0,
});

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ssoUrl(companyId: string, returnTo: string): string {
  return `/api/auth/login?${new URLSearchParams({ company: companyId, returnTo }).toString()}`;
}

/**
 * Login per company (ADR-0010, ADR-0041): with its SSO provider, with email and password, or
 * either way. The company chosen last on this device opens directly; one that only allows SSO
 * goes straight to the provider. The page wears the platform design, not a company brand.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const search: LoginSearch = useSearch({ strict: false });
  const client = useQueryClient();
  const { data, isPending, isError, refetch } = useQuery(loginCompaniesQuery);
  const [chosen, setChosen] = useState<string | null>(search.company ?? null);
  const returnTo = search.returnTo ?? '/start';

  const single = data?.companies.length === 1 ? data.companies[0]!.id : null;
  const company = data?.companies.find((c) => c.id === (chosen ?? data.remembered ?? single));
  // Straight to the provider when it is the only way in, unless the last attempt failed.
  const redirecting = !!company?.sso && !company.password && !search.error;

  useEffect(() => {
    if (redirecting && company) window.location.assign(ssoUrl(company.id, returnTo));
  }, [redirecting, company, returnTo]);

  async function forgetCompany() {
    setChosen(null);
    await api.post('/auth/forget-company').catch(() => undefined);
    client.setQueryData(loginCompaniesQuery.queryKey, (old) =>
      old ? { ...old, remembered: null } : old,
    );
  }

  const errorText = search.error
    ? (LOGIN_ERROR_CODES as readonly string[]).includes(search.error)
      ? t(`login.errors.${search.error}`)
      : t('login.errors.unknown')
    : null;

  return (
    <PublicFrame
      title={t('login.title')}
      description={company ? undefined : t('login.chooseCompanyHint')}
    >
      <div className="mt-6 flex flex-col gap-3">
        {errorText && (
          <Banner tone="danger" role="alert">
            {errorText}
          </Banner>
        )}
        {search.expired && !errorText && <Banner tone="info">{t('shell.sessionEnded')}</Banner>}
        {search.passwordSet && <Banner tone="success">{t('login.passwordSet')}</Banner>}
        {isError && (
          <Banner
            tone="danger"
            role="alert"
            action={
              <Button size="sm" onClick={() => void refetch()}>
                {t('ui.retry')}
              </Button>
            }
          >
            {t('common.loadFailed')}
          </Banner>
        )}
      </div>

      {isPending ? (
        <div className="mt-4 flex flex-col gap-4 rounded-lg border border-line bg-surface p-4">
          <Skeleton className="h-7 w-2/3" />
          <Skeleton className="h-7 w-1/2" />
        </div>
      ) : company ? (
        <CompanyLogin
          key={company.id}
          company={company}
          returnTo={returnTo}
          redirecting={redirecting}
          onOtherCompany={single ? undefined : () => void forgetCompany()}
        />
      ) : data ? (
        <section
          aria-labelledby="login-companies"
          className="mt-4 overflow-hidden rounded-lg border border-line bg-surface"
        >
          <h2
            id="login-companies"
            className="border-b border-line px-4 py-3 text-sm font-semibold text-fg"
          >
            {t('login.chooseCompany')}
          </h2>
          {data.companies.length === 0 ? (
            <p className="p-4 text-fg-muted">{t('login.noCompanies')}</p>
          ) : (
            <ul className="divide-y divide-line">
              {data.companies.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setChosen(item.id)}
                    className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors transition-fast hover:bg-hover"
                  >
                    <CompanyMark company={item} />
                    <span className="min-w-0 flex-1 truncate font-medium text-fg">{item.name}</span>
                    <Icon name="chevron-right" className="text-fg-subtle" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </PublicFrame>
  );
}

type Step = 'password' | 'two-factor' | 'passkey' | 'forgot';

/** The ways into one company: SSO button, password form, second step, forgotten password. */
function CompanyLogin({
  company,
  returnTo,
  redirecting,
  onOtherCompany,
}: {
  company: LoginCompany;
  returnTo: string;
  redirecting: boolean;
  onOtherCompany?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  // Why the password step shows again, e.g. the second factor took too long.
  const [notice, setNotice] = useState<string | null>(null);

  async function signedIn() {
    // Nothing loaded for an earlier session stays.
    client.removeQueries({ predicate: (query) => query.queryKey[0] !== 'login' });
    await navigate({ href: returnTo, replace: true });
  }

  function show(next: Step, reason: string | null = null) {
    setNotice(reason);
    setStep(next);
  }

  return (
    <section
      aria-labelledby="login-company"
      className="mt-4 overflow-hidden rounded-lg border border-line bg-surface"
    >
      <div className="flex items-center gap-3 border-b border-line px-4 py-3">
        <CompanyMark company={company} />
        <h2 id="login-company" className="line-clamp-2 min-w-0 flex-1 font-semibold text-fg">
          {company.name}
        </h2>
        {onOtherCompany && (
          <Button
            variant="ghost"
            size="sm"
            icon="chevrons-up-down"
            iconOnlyOnPhone
            onClick={onOtherCompany}
          >
            {t('login.otherCompany')}
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4 p-4">
        {redirecting ? (
          <p className="flex items-center gap-2.5 text-fg">
            <Spinner />
            {t('login.redirecting', { company: company.name })}
          </p>
        ) : step === 'two-factor' ? (
          <TwoFactorStep
            onSignedIn={signedIn}
            onExpired={(reason) => show('password', reason)}
            onCancel={() => show('password')}
          />
        ) : step === 'passkey' ? (
          <PasskeyStep onSignedIn={signedIn} onCancel={() => show('password')} />
        ) : step === 'forgot' ? (
          <ForgotStep
            company={company}
            email={email}
            onEmail={setEmail}
            onBack={() => show('password')}
          />
        ) : (
          <>
            {company.sso && (
              <Button
                variant="primary"
                icon="log-in"
                block
                onClick={() => window.location.assign(ssoUrl(company.id, returnTo))}
              >
                {t('login.withSso')}
              </Button>
            )}
            {company.sso && company.password && (
              <div className="flex items-center gap-3 text-sm text-fg-subtle">
                <Separator className="flex-1" />
                {t('login.or')}
                <Separator className="flex-1" />
              </div>
            )}
            {company.password && (
              <PasswordStep
                company={company}
                email={email}
                onEmail={setEmail}
                notice={notice}
                primary={!company.sso}
                onSignedIn={signedIn}
                onTwoFactor={() => show('two-factor')}
                onPasskey={() => show('passkey')}
                onForgot={() => show('forgot')}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

function PasswordStep({
  company,
  email,
  onEmail,
  notice,
  primary,
  onSignedIn,
  onTwoFactor,
  onPasskey,
  onForgot,
}: {
  company: LoginCompany;
  email: string;
  onEmail: (email: string) => void;
  notice: string | null;
  /** Without SSO the form is the main way in. */
  primary: boolean;
  onSignedIn: () => Promise<void>;
  onTwoFactor: () => void;
  /** The password was right, the passkey of the account is the second step. */
  onPasskey: () => void;
  onForgot: () => void;
}) {
  const { t } = useTranslation();
  const errorText = usePasswordError();
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const signIn = useMutation({
    mutationFn: () =>
      api.post<{ status: 'signed-in' | 'two-factor' }>('/auth/password/sign-in', {
        companyId: company.id,
        email: email.trim(),
        password,
      }),
    onSuccess: async ({ status }) => {
      if (status === 'two-factor') onTwoFactor();
      else await onSignedIn();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.message === 'passkey-required') onPasskey();
    },
  });
  const passkey = useMutation({ mutationFn: () => signInWithPasskey(), onSuccess: onSignedIn });
  // Error of the passkey picked in the address field.
  const [autofillError, setAutofillError] = useState<unknown>(null);
  const autofillSignedIn = useEffectEvent(onSignedIn);

  // Password managers such as 1Password offer the passkey right in the address field.
  useEffect(() => {
    let active = true;
    void passkeyAutofillSupported().then(async (supported) => {
      if (!active || !supported) return;
      try {
        await signInWithPasskey(true);
        await autofillSignedIn();
      } catch (error) {
        if (active && !isPasskeyCancelled(error)) setAutofillError(error);
      }
    });
    return () => {
      active = false;
      cancelPasskeyPrompt();
    };
  }, []);

  const emailError = !touched
    ? undefined
    : !email.trim()
      ? t('ui.required')
      : !EMAIL.test(email.trim())
        ? t('login.emailInvalid')
        : undefined;
  const passkeyError = [passkey.error, autofillError].find(
    (error) => error && !isPasskeyCancelled(error),
  );
  const failure = signIn.error
    ? errorText(signIn.error)
    : passkeyError
      ? errorText(passkeyError)
      : signIn.isIdle
        ? notice
        : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (EMAIL.test(email.trim()) && password) signIn.mutate();
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      {failure && (
        <Banner tone="danger" role="alert">
          {failure}
        </Banner>
      )}
      <FormField label={t('login.email')} required error={emailError}>
        {(field) => (
          <Input
            {...field}
            type="email"
            inputMode="email"
            // `webauthn`: the browser offers passkeys here too (conditional UI).
            autoComplete="username webauthn"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(event) => onEmail(event.target.value)}
          />
        )}
      </FormField>
      <FormField
        label={t('login.password')}
        required
        error={touched && !password ? t('ui.required') : undefined}
      >
        {(field) => (
          <Input
            {...field}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        )}
      </FormField>
      <div className="flex flex-col-reverse gap-2 md:flex-row md:items-center md:justify-between">
        <Button variant="ghost" onClick={onForgot}>
          {t('login.forgot')}
        </Button>
        <Button
          type="submit"
          variant={primary ? 'primary' : 'secondary'}
          loading={signIn.isPending || signIn.isSuccess}
        >
          {t('login.signIn')}
        </Button>
      </div>
      {passkeysSupported() && (
        <Button
          icon="fingerprint"
          block
          loading={passkey.isPending || passkey.isSuccess}
          onClick={() => {
            setAutofillError(null);
            passkey.mutate();
          }}
        >
          {t('login.withPasskey')}
        </Button>
      )}
    </form>
  );
}

/** Second step with a code of the authenticator app or one of the backup codes. */
function TwoFactorStep({
  onSignedIn,
  onExpired,
  onCancel,
}: {
  onSignedIn: () => Promise<void>;
  onExpired: (reason: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const errorText = usePasswordError();
  const [kind, setKind] = useState<'totp' | 'backup'>('totp');
  const [code, setCode] = useState('');
  const [touched, setTouched] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const verify = useMutation({
    mutationFn: () => api.post('/auth/password/two-factor', { code: code.trim(), kind }),
    onSuccess: onSignedIn,
    onError: (error) => {
      if (error instanceof ApiError && error.message === 'two-factor-expired') {
        onExpired(errorText(error));
      }
    },
  });
  // A passkey of the account replaces the code (ADR-0044).
  const passkey = useMutation({ mutationFn: () => signInWithPasskey(), onSuccess: onSignedIn });
  const failure =
    verify.error ?? (passkey.error && !isPasskeyCancelled(passkey.error) ? passkey.error : null);

  // The field replaces the form the focus was in.
  useEffect(() => input.current?.focus(), [kind]);

  const valid = kind === 'totp' ? /^\d{6}$/.test(code) : code.trim().length > 0;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (valid) verify.mutate();
  }

  function switchKind() {
    setKind(kind === 'totp' ? 'backup' : 'totp');
    setCode('');
    setTouched(false);
    verify.reset();
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      <div>
        <h3 className="font-semibold text-fg">{t(`login.twoFactor.${kind}.title`)}</h3>
        <p className="mt-0.5 text-fg-muted">{t(`login.twoFactor.${kind}.hint`)}</p>
      </div>
      {failure && (
        <Banner tone="danger" role="alert">
          {errorText(failure)}
        </Banner>
      )}
      <FormField
        label={t(`login.twoFactor.${kind}.code`)}
        required
        error={
          touched && !valid
            ? t(kind === 'totp' ? 'login.twoFactor.totp.invalid' : 'ui.required')
            : undefined
        }
      >
        {(field) =>
          kind === 'totp' ? (
            <Input
              {...field}
              ref={input}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="font-mono tracking-widest"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            />
          ) : (
            <Input
              {...field}
              ref={input}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={32}
              className="font-mono"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          )
        }
      </FormField>
      <div className="flex flex-col-reverse gap-2 md:flex-row md:items-center md:justify-between">
        <Button variant="ghost" onClick={switchKind}>
          {t(kind === 'totp' ? 'login.twoFactor.useBackup' : 'login.twoFactor.useApp')}
        </Button>
        <Button type="submit" variant="primary" loading={verify.isPending || verify.isSuccess}>
          {t('login.twoFactor.confirm')}
        </Button>
      </div>
      {passkeysSupported() && (
        <Button
          icon="fingerprint"
          block
          loading={passkey.isPending || passkey.isSuccess}
          onClick={() => {
            verify.reset();
            passkey.mutate();
          }}
        >
          {t('login.twoFactor.usePasskey')}
        </Button>
      )}
      <Button variant="ghost" className="self-start" onClick={onCancel}>
        {t('login.backToLogin')}
      </Button>
    </form>
  );
}

/**
 * Second step of an account with passkey and without authenticator app (ADR-0044): the password
 * was right, but only the passkey opens the session. The prompt of the browser opens right away;
 * where the browser wants a click first, the button opens it.
 */
function PasskeyStep({
  onSignedIn,
  onCancel,
}: {
  onSignedIn: () => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const errorText = usePasswordError();
  const heading = useRef<HTMLHeadingElement>(null);
  const supported = passkeysSupported();
  const passkey = useMutation({ mutationFn: () => signInWithPasskey(), onSuccess: onSignedIn });
  const start = useEffectEvent(() => passkey.mutate());

  useEffect(() => {
    // The step replaces the form the focus was in.
    heading.current?.focus();
    if (supported) start();
    return () => cancelPasskeyPrompt();
  }, [supported]);

  const failure = passkey.error && !isPasskeyCancelled(passkey.error) ? passkey.error : null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 ref={heading} tabIndex={-1} className="font-semibold text-fg outline-none">
          {t('login.passkeyStep.title')}
        </h3>
        <p className="mt-0.5 text-fg-muted">{t('login.passkeyStep.hint')}</p>
      </div>
      {failure && (
        <Banner tone="danger" role="alert">
          {errorText(failure)}
        </Banner>
      )}
      {supported ? (
        <Button
          variant="primary"
          icon="fingerprint"
          block
          loading={passkey.isPending || passkey.isSuccess}
          onClick={() => passkey.mutate()}
        >
          {t('login.passkeyStep.confirm')}
        </Button>
      ) : (
        <Banner tone="info">{t('login.passkeyStep.unsupported')}</Banner>
      )}
      <Button variant="ghost" className="self-start" onClick={onCancel}>
        {t('login.backToLogin')}
      </Button>
    </div>
  );
}

/** Request of a link to set a new password. Answers the same for known and unknown addresses. */
function ForgotStep({
  company,
  email,
  onEmail,
  onBack,
}: {
  company: LoginCompany;
  email: string;
  onEmail: (email: string) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const errorText = usePasswordError();
  const [touched, setTouched] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const forgot = useMutation({
    mutationFn: () =>
      api.post('/auth/password/forgot', { companyId: company.id, email: email.trim() }),
  });

  useEffect(() => input.current?.focus(), []);

  const emailError = !touched
    ? undefined
    : !email.trim()
      ? t('ui.required')
      : !EMAIL.test(email.trim())
        ? t('login.emailInvalid')
        : undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (EMAIL.test(email.trim())) forgot.mutate();
  }

  if (forgot.isSuccess) {
    return (
      <>
        <Banner tone="success" role="status">
          {t('login.forgotSent')}
        </Banner>
        <Button className="self-start" onClick={onBack}>
          {t('login.backToLogin')}
        </Button>
      </>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      <div>
        <h3 className="font-semibold text-fg">{t('login.forgotTitle')}</h3>
        <p className="mt-0.5 text-fg-muted">{t('login.forgotHint')}</p>
      </div>
      {forgot.error && (
        <Banner tone="danger" role="alert">
          {errorText(forgot.error)}
        </Banner>
      )}
      <FormField label={t('login.email')} required error={emailError}>
        {(field) => (
          <Input
            {...field}
            ref={input}
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(event) => onEmail(event.target.value)}
          />
        )}
      </FormField>
      <div className="flex flex-col-reverse gap-2 md:flex-row md:items-center md:justify-between">
        <Button variant="ghost" onClick={onBack}>
          {t('login.backToLogin')}
        </Button>
        <Button type="submit" variant="primary" loading={forgot.isPending}>
          {t('login.sendLink')}
        </Button>
      </div>
    </form>
  );
}
